import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
    Checkbox,
    FormControl,
    FormControlLabel,
    Grid,
    MenuItem,
    Select,
    Typography,
    makeStyles
} from '@material-ui/core';
import { ApplicationState } from '../../reducers';
import { PrintoutVersion, ExportOptions, ExportType } from '../../domain/domain';
import { SECTION_AND_BELOW_NODES } from './exportConstants';
import EditorialContentSelectPanel from './EditorialContentSelectPanel';
import { getVisibleEditorialTypes, setEditorialTypeSelected } from './editorialContentHelpers';

const useStyles = makeStyles(_ => ({
    root: {
        overflow: 'hidden'
    },
    select: {
        width: 220
    },
    field: {
        width: 220
    }
}));

type ComponentSelectionPanelProps = {
    exportOptions: ExportOptions,
    onExportOptionsChanged: (exportOptions: ExportOptions) => void
}

const ComponentSelectionPanel: React.FC<ComponentSelectionPanelProps> = ({ exportOptions, onExportOptionsChanged }) => {
    const classes = useStyles();
    const isCertifiedCopy = exportOptions.exportType === ExportType.CERTIFIED_COPY;
    const isHeadingOnly = exportOptions.exportType === ExportType.HEADING_ONLY;
    const isProvisionsPrintout = exportOptions.exportType === ExportType.PROVISIONS_PRINTOUT;

    const subTree = useSelector((state: ApplicationState) => state.ui.printingOptionsDialogSubTree);
    const nodeType = subTree?.nodeType;

    const currentUser = useSelector((app: ApplicationState) => app.user.currentUser);

    const showHideHeadingsOption = isProvisionsPrintout &&
        (exportOptions.statuteVersion === PrintoutVersion.LAST_APPROVED_VERSION ||
            exportOptions.statuteVersion === PrintoutVersion.WORKING_COPY_VERSION) &&
        nodeType !== undefined &&
        !SECTION_AND_BELOW_NODES.includes(nodeType);

    const shouldHideNoneOption = isCertifiedCopy || isHeadingOnly;

    useEffect(() => {
        // Only run when export type changes, not on every render or selection change
        if (isProvisionsPrintout && exportOptions.exportType === ExportType.PROVISIONS_PRINTOUT) {
            // Only update editorial types, NOT statute version
            const visibleTypes = getVisibleEditorialTypes(currentUser);
            const shouldCheck = exportOptions.editorialContentPrintoutVersion !== PrintoutVersion.NONE;

            // Only modify editorial types, preserve user's version selections
            const updatedOptions = visibleTypes.reduce(
                (options, { type }) => setEditorialTypeSelected(options, type, shouldCheck),
                { ...exportOptions }
            );

            // Only apply if the editorial types changed
            if (JSON.stringify(updatedOptions.editorialTypes) !== JSON.stringify(exportOptions.editorialTypes)) {
                onExportOptionsChanged(updatedOptions);
            }
        }
    }, [exportOptions.exportType, currentUser, isProvisionsPrintout]);

    useEffect(() => {
        if (isCertifiedCopy) {
            const hasWorkingCopyStatute = exportOptions.statuteVersion === PrintoutVersion.WORKING_COPY_VERSION;
            const hasWorkingCopyEditorial = exportOptions.editorialContentPrintoutVersion === PrintoutVersion.WORKING_COPY_VERSION;

            if (hasWorkingCopyStatute || hasWorkingCopyEditorial) {
                onExportOptionsChanged({
                    ...exportOptions,
                    statuteVersion: hasWorkingCopyStatute ? PrintoutVersion.LAST_APPROVED_VERSION : exportOptions.statuteVersion,
                    editorialContentPrintoutVersion: hasWorkingCopyEditorial ? PrintoutVersion.LAST_APPROVED_VERSION : exportOptions.editorialContentPrintoutVersion
                });
            }
        }
    }, [isCertifiedCopy, exportOptions.statuteVersion, exportOptions.editorialContentPrintoutVersion]);

    const handleStatuteVersionChange = (newVersion: PrintoutVersion) => {
        onExportOptionsChanged({
            ...exportOptions,
            statuteVersion: newVersion,
            hideHeadingsAndAnalyses: newVersion === PrintoutVersion.NONE ? false : !!exportOptions.hideHeadingsAndAnalyses
        });
    };

    const handleEditorialContentVersionChange = (newVersion: PrintoutVersion) => {
        const visibleTypes = getVisibleEditorialTypes(currentUser);
        const shouldCheck = newVersion !== PrintoutVersion.NONE;

        const updatedOptions = visibleTypes.reduce(
            (options, { type }) => setEditorialTypeSelected(options, type, shouldCheck),
            { ...exportOptions, editorialContentPrintoutVersion: newVersion }
        );

        onExportOptionsChanged(updatedOptions);
    };

    return (
        <Grid container direction='row' className="flex-gap-2">
            <Grid item>
                <Grid container direction='column' className="flex-gap-1">
                    <Grid item>
                        <Grid container direction='column' className="flex-gap-1">
                            <Grid item>
                                <Typography>Statute Version</Typography>
                            </Grid>
                            <Grid item>
                                <FormControl size='small'>
                                    <Select
                                        id='statute-version-select'
                                        variant='outlined'
                                        value={exportOptions.statuteVersion}
                                        className={classes.select}
                                        style={{ width: 300 }}
                                        onChange={(event) => {
                                            if (event?.target?.value) {
                                                handleStatuteVersionChange(event.target.value as PrintoutVersion)
                                            }
                                        }}
                                    >
                                        <MenuItem value={PrintoutVersion.LAST_APPROVED_VERSION}>Last Approved Version</MenuItem>
                                        {!isCertifiedCopy &&
                                            <MenuItem value={PrintoutVersion.WORKING_COPY_VERSION}>Working Copy Version</MenuItem>
                                        }
                                        {!shouldHideNoneOption &&
                                            <MenuItem value={PrintoutVersion.NONE}>None</MenuItem>
                                        }
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Grid>
                    {showHideHeadingsOption && (
                        <Grid item>
                            <Grid container direction='column' className="flex-gap-1">
                                <Grid item>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                size='small'
                                                checked={!!exportOptions.hideHeadingsAndAnalyses}
                                                onChange={(event) => {
                                                    onExportOptionsChanged({
                                                        ...exportOptions,
                                                        hideHeadingsAndAnalyses: event.target.checked
                                                    });
                                                }}
                                            />
                                        }
                                        label="Hide Headings and Analyses"
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    )}
                    <Grid item></Grid>
                    <Grid item>
                        <Grid container direction='column' className="flex-gap-1" wrap='nowrap'>
                            <Grid item>
                                <Typography>Editorial Content Version</Typography>
                            </Grid>
                            <Grid item>
                                <FormControl size='small'>
                                    <Select
                                        id='editorial-content-version-select'
                                        variant='outlined'
                                        value={exportOptions.editorialContentPrintoutVersion}
                                        className={classes.select}
                                        style={{ width: 300 }}
                                        onChange={(event) => {
                                            if (event?.target?.value) {
                                                handleEditorialContentVersionChange(event.target.value as PrintoutVersion);
                                            }
                                        }}
                                    >
                                        <MenuItem value={PrintoutVersion.LAST_APPROVED_VERSION}>Last Approved Version</MenuItem>
                                        {!isCertifiedCopy &&
                                            <MenuItem value={PrintoutVersion.WORKING_COPY_VERSION}>Working Copy Version</MenuItem>
                                        }
                                        <MenuItem value={PrintoutVersion.NONE}>None</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Grid>
                    {exportOptions.editorialContentPrintoutVersion !== PrintoutVersion.NONE &&
                        <>
                            <Grid item></Grid>
                            <Grid item>
                                <EditorialContentSelectPanel exportOptions={exportOptions} onExportOptionsChanged={onExportOptionsChanged} />
                            </Grid>
                        </>
                    }
                </Grid>
            </Grid>
        </Grid>
    )
}

export default ComponentSelectionPanel;